import { useClickOutside } from "@/hooks/useClickOutside";
import { useRef, useState, type ReactNode } from "react";

interface PopoverProps {
  trigger: ReactNode;
  content: ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
  className?: string;
}

const placementClasses: Record<string, string> = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
  left: "right-full top-1/2 -translate-y-1/2 mr-2",
  right: "left-full top-1/2 -translate-y-1/2 ml-2",
};

/**
 * Click-triggered floating content panel.
 * More flexible than Tooltip — renders arbitrary ReactNode.
 */
export function Popover({
  trigger,
  content,
  placement = "bottom",
  className = "",
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => setOpen(false));

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex"
      >
        {trigger}
      </button>

      {open && (
        <div
          role="dialog"
          className={`absolute z-50 min-w-50 rounded-lg border border-border bg-bg-card p-3 shadow-xl ${placementClasses[placement]}`}
        >
          {content}
        </div>
      )}
    </div>
  );
}
